import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { trackEvent } from '@Utilities/marketingTags'
import { FACEBOOK_GROUP_URL, WHATSAPP_CHANNEL_URL } from '@Components/Layout/Footer/Footer'
import FreeLessonShot from '../Assets/free-lesson.jpg'
import { Container, MOBILE, NavyOutlinePill, NavyPill } from '../CustomLanding.styled'
import { colors, freeTrial } from '../content'
import { scrollToClassrooms, useClassroomsVisible } from './ClassroomsCarousel'

const FreeTrial: React.FC = () => {
	const navigate = useNavigate()
	const classroomsVisible = useClassroomsVisible()

	const handleFreeLesson = () => {
		trackEvent('CTAClick', { cta_text: 'free_lesson', cta_location: 'free_trial' })
		navigate(freeTrial.freeLessonPath)
	}

	return (
		<Section id="start">
			<Split>
				<TextBlock>
					<Title>
						{freeTrial.title}
						<span>{freeTrial.titleEmoji}</span>
					</Title>
					<P>{freeTrial.p1}</P>
					<P last>{freeTrial.p2}</P>
				</TextBlock>
				<VideoBox>
					<VideoImg src={FreeLessonShot} alt={freeTrial.videoCaption} />
				</VideoBox>
				<Actions>
					<NavyPill onClick={handleFreeLesson}>{freeTrial.ctaPrimary}</NavyPill>
					{classroomsVisible && (
						<NavyOutlinePill onClick={() => scrollToClassrooms(freeTrial.ctaSecondary)}>{freeTrial.ctaSecondary}</NavyOutlinePill>
					)}
					<GroupLine>
						{freeTrial.groupBefore}
						<a href={FACEBOOK_GROUP_URL} target="_blank" rel="noreferrer">
							{freeTrial.groupCta}
						</a>
						{freeTrial.groupOr}
						<a href={WHATSAPP_CHANNEL_URL} target="_blank" rel="noreferrer">
							{freeTrial.channelCta}
						</a>
					</GroupLine>
				</Actions>
			</Split>
		</Section>
	)
}

export default FreeTrial

const Section = styled.section`
	background: ${colors.amber};
	padding: 48px 0;
	${MOBILE} {
		padding: 40px 0;
	}
`

/* Desktop: text + buttons on one side, video box on the other.
   Mobile (per artboard): text, then video box, then stacked buttons. */
const Split = styled(Container)`
	display: grid;
	grid-template-columns: 1.4fr 1fr;
	grid-template-areas:
		'text video'
		'actions video';
	gap: 18px 48px;
	align-items: center;
	${MOBILE} {
		display: block;
	}
`

const TextBlock = styled.div`
	grid-area: text;
	min-width: 0;
`

const Title = styled.h2`
	font-weight: 800;
	font-size: clamp(24px, 2.2vw, 34px);
	line-height: 1.32;
	color: ${colors.navy};
	margin: 0 0 24px;
	${MOBILE} {
		font-size: 29px;
		line-height: 1.35;
		margin-bottom: 16px;
	}
`

const P = styled.p<{ last?: boolean }>`
	margin: 0 0 ${p => (p.last ? '12px' : '14px')};
	font-size: 17px;
	line-height: 1.95;
	color: rgba(22, 58, 132, 0.85);
	${MOBILE} {
		font-size: 16px;
		line-height: 1.9;
		margin-bottom: ${p => (p.last ? '24px' : '10px')};
	}
`

/* Real course-player mockup shown at its own ratio inside the rounded frame. */
const VideoBox = styled.div`
	grid-area: video;
	min-width: 0;
	max-width: 420px;
	justify-self: center;
	border-radius: 20px;
	overflow: hidden;
	border: 1px solid rgba(22, 58, 132, 0.28);
	background-color: rgba(22, 58, 132, 0.1);
	${MOBILE} {
		max-width: none;
		border-radius: 20px;
		margin-bottom: 24px;
	}
`

const VideoImg = styled.img`
	display: block;
	width: 100%;
	height: auto;
`

const Actions = styled.div`
	grid-area: actions;
	display: flex;
	flex-wrap: wrap;
	gap: 14px;
	align-self: start;
	${MOBILE} {
		flex-direction: column;
		gap: 10px;
	}
`

const GroupLine = styled.p`
	width: 100%;
	margin: 4px 0 0;
	font-size: 14px;
	color: rgba(22, 58, 132, 0.85);
	a {
		color: ${colors.navy};
		font-weight: 700;
		text-decoration: underline;
	}
	${MOBILE} {
		text-align: center;
		font-size: 14px;
	}
`
